import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Heart } from "lucide-react";

type Particle = {
  id: number;
  startX: number;
  startY: number;
  duration: number;
  delay: number;
  size: number;
  // pre-computed keyframe values (5 points, matched to shared times array)
  yKeys: [number, number, number, number, number];
  xKeys: [number, number, number, number, number];
};

// Shared keyframe timing: fade-in over first 12%, visible until 80%, fade-out last 20%
const TIMES: [number, number, number, number, number] = [0, 0.12, 0.45, 0.80, 1];

let nextId = 1;

export function FloatingHearts({ trigger }: { trigger: number }) {
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    if (!trigger) return;
    const vh = window.innerHeight;
    const spawned: Particle[] = Array.from({ length: 10 }, () => {
      const yTravel = vh * (0.5 + Math.random() * 0.25);
      const s = Math.random() < 0.5 ? 1 : -1;
      const drift = (40 + Math.random() * 50) * s; // drift one direction, no reversals
      return {
        id: nextId++,
        startX: 8 + Math.random() * 84,
        startY: 68 + Math.random() * 26,
        duration: 1.1 + Math.random() * 0.6,
        delay: Math.random() * 0.4,
        size: 18 + Math.random() * 18,
        // y: proportional to times → constant upward velocity
        yKeys: [0, -yTravel * 0.12, -yTravel * 0.45, -yTravel * 0.80, -yTravel],
        // x: smooth one-direction drift with slight organic variation
        xKeys: [0, drift * 0.2, drift * 0.5, drift * 0.8, drift],
      };
    });
    setParticles((prev) => [...prev, ...spawned]);
    const t = setTimeout(() => {
      setParticles((prev) => prev.filter((p) => !spawned.some((s) => s.id === p.id)));
    }, 2500);
    return () => clearTimeout(t);
  }, [trigger]);

  if (!particles.length) return null;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-[9997]">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{ left: `${p.startX}%`, top: `${p.startY}%` }}
          animate={{
            opacity: [0, 1, 1, 1, 0],
            y: p.yKeys,
            x: p.xKeys,
            scale: [0.3, 1.15, 1.05, 0.95, 0.85],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: "linear",
            times: TIMES, // single shared times array — all 5 keyframe arrays stay in sync
          }}
        >
          <Heart className="fill-rose-500 text-rose-500" style={{ width: p.size, height: p.size }} />
        </motion.div>
      ))}
    </div>
  );
}
